// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import {
    ProductInventoryDto,
    ProductModel,
    RealTimeInventoryModel,
    RealTimePricingModel,
} from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

interface Parameter {
    productId: string;
    parentProductId: string;
    qtyOrdered?: number;
    unitOfMeasure?: string;
}

/**
 * Reducer Structure:
 * {
 *  variantChildren: {
 *      [parentProductId]: {
 *          productInfosByProductId: {},
 *          products: {},
 *      }
 *  }
 * }
 */
type ProductData = {
    productInfosByProductId: Record<string, ProductInfo>;
    products?: ProductModel[];
};

export type xProductListVariantChildrenStateType = Readonly<{
    variantChildren: Record<string, ProductData>;
}>;

export const initialVariantChildren: xProductListVariantChildrenStateType = {
    variantChildren: {},
};

const reducer = {
    "xProductListVariantChildren/CompleteLoadProducts": (
        draft: Draft<xProductListVariantChildrenStateType>,
        action: { variantChildrenProducts: ProductData["products"] } & Pick<Parameter, "parentProductId">,
    ) => {
        draft.variantChildren[action.parentProductId] = {
            products: action.variantChildrenProducts,
            productInfosByProductId: {},
        };
    },
    "xProductListVariantChildren/UpdateProductInfo": (
        draft: Draft<xProductListVariantChildrenStateType>,
        action: Pick<Parameter, "productId" | "parentProductId" | "qtyOrdered" | "unitOfMeasure">,
    ) => {
        const product = draft.variantChildren[action.parentProductId]?.productInfosByProductId[action.productId];

        if (!product) {
            return;
        }

        // Update QtyOrdered
        if (action.qtyOrdered) {
            product.qtyOrdered = action.qtyOrdered;
        }

        // Update UnitOfMeasure
        if (action.unitOfMeasure) {
            product.unitOfMeasure = action.unitOfMeasure;
        }
    },
    "xProductListVariantChildren/CompleteLoadProductInfos": (
        draft: Draft<xProductListVariantChildrenStateType>,
        action: { productInfosByProductId: ProductData["productInfosByProductId"] } & Pick<
            Parameter,
            "parentProductId"
        >,
    ) => {
        if (!draft.variantChildren[action.parentProductId]?.productInfosByProductId) {
            return;
        }

        draft.variantChildren[action.parentProductId]!.productInfosByProductId = action.productInfosByProductId;
    },
    "xProductListVariantChildren/LoadingRealTimePricing": (
        draft: Draft<xProductListVariantChildrenStateType>,
        action: { realTimePricing: RealTimePricingModel } & Pick<Parameter, "parentProductId" | "productId">,
    ) => {
        const productInfo =
            draft.variantChildren?.[action.parentProductId]?.productInfosByProductId?.[action.productId];
        if (!productInfo) {
            return;
        }

        productInfo.pricing = undefined;
        delete productInfo.failedToLoadPricing;
    },
    "xProductListVariantChildren/CompleteLoadRealTimePricing": (
        draft: Draft<xProductListVariantChildrenStateType>,
        action: { realTimePricing: RealTimePricingModel } & Pick<Parameter, "parentProductId">,
    ) => {
        const productInfosByProductId = draft.variantChildren[action.parentProductId]?.productInfosByProductId;
        if (!productInfosByProductId || !action.realTimePricing.realTimePricingResults) {
            return;
        }

        action.realTimePricing.realTimePricingResults.forEach(pricing => {
            const productInfo = productInfosByProductId[pricing.productId];
            if (!productInfo) {
                return;
            }

            productInfo.pricing = pricing;
            delete productInfo.failedToLoadPricing;
        });
    },
    "xProductListVariantChildren/FailedLoadRealTimePricing": (
        draft: Draft<xProductListVariantChildrenStateType>,
        action: Pick<Parameter, "parentProductId">,
    ) => {
        const productInfosByProductId = draft.variantChildren[action.parentProductId]?.productInfosByProductId;
        if (!productInfosByProductId) {
            return;
        }

        for (const productId in productInfosByProductId) {
            productInfosByProductId[productId]!.failedToLoadPricing = true;
        }
    },
    "xProductListVariantChildren/CompleteLoadRealTimeInventory": (
        draft: Draft<xProductListVariantChildrenStateType>,
        action: { realTimeInventory: RealTimeInventoryModel } & Pick<Parameter, "parentProductId">,
    ) => {
        const productInfosByProductId = draft.variantChildren[action.parentProductId]?.productInfosByProductId;
        if (!productInfosByProductId || !action.realTimeInventory?.realTimeInventoryResults) {
            return;
        }

        action.realTimeInventory.realTimeInventoryResults.forEach(inventory => {
            const productInfo = productInfosByProductId[inventory.productId];
            if (!productInfo) {
                return;
            }

            productInfo.inventory = inventory;
        });
    },
    "xProductListVariantChildren/FailedLoadRealTimeInventory": (
        draft: Draft<xProductListVariantChildrenStateType>,
        action: Pick<Parameter, "parentProductId">,
    ) => {
        const productInfosByProductId = draft.variantChildren[action.parentProductId]?.productInfosByProductId;
        if (!productInfosByProductId) {
            return;
        }

        for (const productId in productInfosByProductId) {
            productInfosByProductId[productId]!.failedToLoadInventory = true;
        }
    },
};

const xProductListVariantChildren = createTypedReducerWithImmer(initialVariantChildren, reducer);

(reducers as any).xProductListVariantChildren = xProductListVariantChildren;

export type xProductListVariantChildrenState = {
    xProductListVariantChildren: xProductListVariantChildrenStateType;
} & ApplicationState;

type xProductListVariantChildrenReducers = {
    [key: string]: typeof xProductListVariantChildren;
};

export type xProductListVariantChildrenActions =
    | Parameters<xProductListVariantChildrenReducers[keyof xProductListVariantChildrenReducers]>[1]
    | AnyAction;
